import React, { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "./components/ui/sonner";

// Error Boundary to prevent blank pages on unexpected errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '' };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || String(error || 'Unbekannter Fehler'),
      errorStack: error?.stack || '',
    };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
    // Best-effort logging so we can diagnose production crashes via DevTools.
    try {
      window.__lastAppError = { message: error?.message, stack: error?.stack, info };
    } catch { /* ignore */ }
  }
  handleReset = () => {
    // Try a soft recovery first — re-mounts the tree without a full reload.
    this.setState({ hasError: false, errorMessage: '', errorStack: '' });
  };
  handleReload = () => window.location.reload();
  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV !== 'production';
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#161313', color: '#fff', fontFamily: 'Manrope, sans-serif', padding: 16 }}>
          <div style={{ textAlign: 'center', maxWidth: 560 }}>
            <h1 style={{ fontSize: 24, marginBottom: 12 }}>Etwas ist schiefgelaufen</h1>
            <p style={{ color: '#a1a1aa', marginBottom: 8 }}>
              Bitte versuche es nochmal oder lade die Seite neu.
            </p>
            {this.state.errorMessage && (
              <p style={{ color: '#f87171', fontSize: 13, marginBottom: 20, fontFamily: 'monospace', background: '#231f1f', padding: '8px 12px', borderRadius: 6, wordBreak: 'break-word' }}>
                {this.state.errorMessage}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={this.handleReset} style={{ background: '#231f1f', color: '#fff', border: '1px solid #605e5e44', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                Erneut versuchen
              </button>
              <button onClick={this.handleReload} style={{ background: '#0094ff', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Seite neu laden
              </button>
            </div>
            {isDev && this.state.errorStack && (
              <pre style={{ marginTop: 20, padding: 12, background: '#0a0a0a', color: '#999', fontSize: 11, textAlign: 'left', maxHeight: 240, overflow: 'auto', borderRadius: 6 }}>
                {this.state.errorStack}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import PresenceHeartbeat from "./components/PresenceHeartbeat";

// Pages — lazily loaded per route so visiting one page doesn't pull in the
// JS for all ~60 others into the initial bundle. LandingPage stays a static
// import since it's the most common entry point (no Suspense flash on "/").
import LandingPage from "./pages/LandingPage";
const LoginPage = lazy(() => import("./pages/AuthPages").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("./pages/AuthPages").then(m => ({ default: m.RegisterPage })));
const AdminLoginPage = lazy(() => import("./pages/AuthPages").then(m => ({ default: m.AdminLoginPage })));
const BusinessLandingPage = lazy(() => import("./pages/business/BusinessLandingPage"));
const BusinessRegisterPage = lazy(() => import("./pages/business/BusinessRegisterPage"));
const BusinessLoginPage = lazy(() => import("./pages/business/BusinessLoginPage"));
const BusinessDashboard = lazy(() => import("./pages/business/BusinessDashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CustomPage = lazy(() => import("./pages/CustomPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const CounterProposalPage = lazy(() => import("./pages/CounterProposalPage"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage").then(m => ({ default: m.ServicesPage })));
const MyWorldLayout = lazy(() => import("./pages/myworld/MyWorldLayout"));
const MyWorldDashboard = lazy(() => import("./pages/myworld/MyWorldDashboard"));
const DetailBuddyPage = lazy(() => import("./pages/myworld/DetailBuddyPage"));
const GaragePage = lazy(() => import("./pages/myworld/GaragePage"));
const TimelinePage = lazy(() => import("./pages/myworld/TimelinePage"));
const KostenPage = lazy(() => import("./pages/myworld/KostenPage"));
const ConciergePage = lazy(() => import("./pages/myworld/ConciergePage"));
const CheckInPage = lazy(() => import("./pages/CheckInPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AppointmentsPage = lazy(() => import("./pages/AppointmentsPage"));
const ImpressumPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.ImpressumPage })));
const AGBPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.AGBPage })));
const DatenschutzPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.DatenschutzPage })));
const FahrzeugannahmePage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.FahrzeugannahmePage })));
const WiderrufPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.WiderrufPage })));
const CareRecordPage = lazy(() => import("./pages/CareRecordPage"));
const MixingCalculatorPage = lazy(() => import("./pages/MixingCalculatorPage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const TorqueSpecsPage = lazy(() => import("./pages/TorqueSpecsPage"));
const LackschadenScannerPage = lazy(() => import("./pages/LackschadenScannerPage"));
const ReifenVerschleissPage = lazy(() => import("./pages/ReifenVerschleissPage"));
const WertverlustRechnerPage = lazy(() => import("./pages/WertverlustRechnerPage"));
const LackCodeFinderPage = lazy(() => import("./pages/LackCodeFinderPage"));
const WashboxFinderPage = lazy(() => import("./pages/WashboxFinderPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const InspektionRechnerPage = lazy(() => import("./pages/InspektionRechnerPage"));
const WertgutachtenPage = lazy(() => import("./pages/WertgutachtenPage"));
const SaisonreifenPage = lazy(() => import("./pages/SaisonreifenPage"));
const KlarlackPage = lazy(() => import("./pages/KlarlackPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const OrderPage = lazy(() => import("./pages/OrderPage"));
const MasterPanel = lazy(() => import("./pages/MasterPanel"));
const MActiveFullscreenPage = lazy(() => import("./pages/MActiveFullscreenPage"));
// import MActivePage from "./pages/MActivePage"; // M-Active ausgelagert
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ConfiguratorPage = lazy(() => import("./pages/ConfiguratorPage"));
const CoveragePage = lazy(() => import("./pages/CoveragePage"));
const CarOfTheMonthPage = lazy(() => import("./pages/CarOfTheMonthPage"));
const HandoverPage = lazy(() => import("./pages/HandoverPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const WashboxRegionPage = lazy(() => import("./pages/seo/WashboxRegionPage"));
const ServicePage = lazy(() => import("./pages/seo/ServicePage"));
const LocationPage = lazy(() => import("./pages/seo/LocationPage"));
const LegacyCityRedirect = lazy(() => import("./pages/seo/LegacyCityRedirect"));
const OneWorldBrandPage = lazy(() => import("./pages/seo/OneWorldBrandPage"));
import { BusinessJsonLd } from "./components/SEO";
import SearchConsoleVerification from "./components/SearchConsoleVerification";
import { SeoOverridesProvider } from "./context/SeoOverridesContext";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#161313] flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#161313] flex items-center justify-center"><div className="spinner" /></div>;
  if (!user || !isAdmin) return <Navigate to="/admin/login" replace />;
  return children;
};

// Master Protected Route
const MasterRoute = ({ children }) => {
  const { user, loading, isMaster } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#161313] flex items-center justify-center"><div className="spinner" /></div>;
  if (!user || !isMaster) return <Navigate to="/admin/login" replace />;
  return children;
};

import PromoBanner from "./components/PromoBanner";
import CookieBanner from "./components/CookieBanner";

// Layout with Header and Footer
const MainLayout = ({ children }) => (
  <>
    <Header />
    <main className="min-h-screen pt-20 sm:pt-24">
      <PromoBanner />
      {children}
    </main>
    <Footer />
    <CookieBanner />
  </>
);

// Admin Layout without Header/Footer
const AdminLayout = ({ children }) => (
  <main className="min-h-screen">{children}</main>
);

// Shown while a lazy route chunk is being fetched.
const RouteFallback = () => (
  <div className="min-h-screen bg-[#161313] flex items-center justify-center"><div className="spinner" /></div>
);

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <BusinessJsonLd />
      <PresenceHeartbeat />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<MainLayout><LandingPage /></MainLayout>} />
          <Route path="/login" element={<MainLayout><LoginPage /></MainLayout>} />
          <Route path="/register" element={<MainLayout><RegisterPage /></MainLayout>} />
          <Route path="/registrieren" element={<MainLayout><RegisterPage /></MainLayout>} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/business" element={<BusinessLandingPage />} />
          <Route path="/business/register" element={<BusinessRegisterPage />} />
          <Route path="/business/login" element={<BusinessLoginPage />} />
          <Route path="/business/dashboard" element={<BusinessDashboard />} />
          <Route path="/passwort-vergessen" element={<MainLayout><ForgotPasswordPage /></MainLayout>} />
          <Route path="/passwort-reset" element={<MainLayout><ResetPasswordPage /></MainLayout>} />

          {/* About & Products */}
          <Route path="/ueber-uns" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/produkte" element={<MainLayout><ProductsPage /></MainLayout>} />
          <Route path="/funktionen" element={<MainLayout><FeaturesPage /></MainLayout>} />

          {/* Services & Pricing (combined) */}
          <Route path="/services" element={<MainLayout><ServicesPage /></MainLayout>} />
          <Route path="/preise" element={<Navigate to="/services" replace />} />

          {/* Booking */}
          <Route path="/termin" element={<MainLayout><BookingPage /></MainLayout>} />
          <Route path="/gegenvorschlag/:token" element={<MainLayout><CounterProposalPage /></MainLayout>} />

          {/* My World */}
          <Route path="/my-world" element={<MainLayout><MyWorldLayout /></MainLayout>}>
            <Route index element={<MyWorldDashboard />} />
            <Route path="garage"    element={<GaragePage />} />
            <Route path="timeline"  element={<TimelinePage />} />
            <Route path="kosten"    element={<KostenPage />} />
            <Route path="punkte"    element={<Navigate to="/my-world" replace />} />
            <Route path="freunde"   element={<DetailBuddyPage />} />
            <Route path="assistent" element={<ConciergePage />} />
            <Route path="news"      element={<Navigate to="/my-world" replace />} />
            <Route path="feedback"  element={<FeedbackPage />} />
          </Route>
          <Route path="/my-world/article/:id" element={<Navigate to="/my-world" replace />} />
          <Route path="/mischrechner" element={<MainLayout><MixingCalculatorPage /></MainLayout>} />
          <Route path="/werkzeuge" element={<MainLayout><ToolsPage /></MainLayout>} />
          {/* /waschboxen ist die alte URL — echter 301 müsste auf Webserver-Ebene
              (nginx auf dem VPS) gesetzt werden, das liegt außerhalb dieses Repos.
              Bis dahin zumindest ein sauberer clientseitiger Redirect statt
              Duplicate Content unter zwei URLs. */}
          <Route path="/waschboxen" element={<Navigate to="/waschbox-finder" replace />} />
          {/* SEO: keyword-richer URL alias for the same component */}
          <Route path="/waschbox-finder" element={<MainLayout><WashboxFinderPage /></MainLayout>} />
          <Route path="/waschbox-finder/:region" element={<MainLayout><WashboxRegionPage /></MainLayout>} />
          {/* SEO: drei Leistungsseiten + zwei Standortseiten statt 55 Stadtseiten.
              Die alten /<leistung>/<stadt>-URLs werden serverseitig per 301
              umgeleitet (nginx); die Routen unten sind nur der App-Fallback. */}
          <Route path="/aufbereiter" element={<MainLayout><ServicePage service="aufbereiter" /></MainLayout>} />
          <Route path="/keramikversiegelung" element={<MainLayout><ServicePage service="keramikversiegelung" /></MainLayout>} />
          <Route path="/lackpolitur" element={<MainLayout><ServicePage service="lackpolitur" /></MainLayout>} />
          <Route path="/standort/:slug" element={<MainLayout><LocationPage /></MainLayout>} />
          <Route path="/fahrzeugpflege" element={<Navigate to="/aufbereiter" replace />} />
          <Route path="/detailing" element={<Navigate to="/aufbereiter" replace />} />
          <Route path="/aufbereiter/:city" element={<LegacyCityRedirect service="aufbereiter" />} />
          <Route path="/fahrzeugpflege/:city" element={<LegacyCityRedirect service="fahrzeugpflege" />} />
          <Route path="/detailing/:city" element={<LegacyCityRedirect service="detailing" />} />
          <Route path="/keramikversiegelung/:city" element={<LegacyCityRedirect service="keramikversiegelung" />} />
          <Route path="/lackpolitur/:city" element={<LegacyCityRedirect service="lackpolitur" />} />
          {/* SEO: Markenseite — fängt „One World"-Suchen ab. Die Ortsvarianten sind zusammengelegt. */}
          <Route path="/one-world" element={<MainLayout><OneWorldBrandPage variant="general" /></MainLayout>} />
          <Route path="/one-world-eppelborn" element={<Navigate to="/standort/eppelborn" replace />} />
          <Route path="/one-world-saarland" element={<Navigate to="/one-world" replace />} />
          <Route path="/drehmoment" element={<MainLayout><TorqueSpecsPage /></MainLayout>} />
          <Route path="/lackschaden-scanner" element={<MainLayout><LackschadenScannerPage /></MainLayout>} />
          <Route path="/reifenverschleiss" element={<MainLayout><ReifenVerschleissPage /></MainLayout>} />
          <Route path="/wertverlust" element={<MainLayout><WertverlustRechnerPage /></MainLayout>} />
          <Route path="/lackcode-finder" element={<MainLayout><LackCodeFinderPage /></MainLayout>} />
          <Route path="/inspektion-rechner" element={<MainLayout><InspektionRechnerPage /></MainLayout>} />
          <Route path="/wertgutachten" element={<MainLayout><WertgutachtenPage /></MainLayout>} />
          <Route path="/saisonreifen" element={<MainLayout><SaisonreifenPage /></MainLayout>} />
          <Route path="/klarlack" element={<MainLayout><KlarlackPage /></MainLayout>} />

          <Route path="/galerie" element={<MainLayout><GalleryPage /></MainLayout>} />
          {/* Frei gestaltete Seiten aus dem Seiten-Builder */}
          <Route path="/seite/:slug" element={<MainLayout><CustomPage /></MainLayout>} />

          {/* Visitor Features */}
          <Route path="/faq" element={<MainLayout><FAQPage /></MainLayout>} />
          <Route path="/konfigurator" element={<MainLayout><ConfiguratorPage /></MainLayout>} />
          <Route path="/einzugsgebiet" element={<MainLayout><CoveragePage /></MainLayout>} />
          <Route path="/fahrzeug-des-monats" element={<MainLayout><CarOfTheMonthPage /></MainLayout>} />
          {/* Kostenrechner abgeschafft — Preise laufen jetzt ausschließlich über
              individuelle Angebote. Redirect statt 404 für alte/indexierte Links. */}
          <Route path="/kostenrechner" element={<Navigate to="/kontakt" replace />} />

          {/* Contact */}
          <Route path="/kontakt" element={<MainLayout><ContactPage /></MainLayout>} />

          {/* Legal Pages */}
          <Route path="/impressum" element={<MainLayout><ImpressumPage /></MainLayout>} />
          <Route path="/agb" element={<MainLayout><AGBPage /></MainLayout>} />
          <Route path="/datenschutz" element={<MainLayout><DatenschutzPage /></MainLayout>} />
          <Route path="/widerruf" element={<MainLayout><WiderrufPage /></MainLayout>} />
          <Route path="/fahrzeugannahme" element={<MainLayout><FahrzeugannahmePage /></MainLayout>} />
          <Route path="/care-record/:recordId" element={<MainLayout><CareRecordPage /></MainLayout>} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
          <Route path="/profil" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
          <Route path="/konto" element={<ProtectedRoute><MainLayout><AccountPage /></MainLayout></ProtectedRoute>} />
          <Route path="/fahrzeuge" element={<ProtectedRoute><MainLayout><VehiclesPage /></MainLayout></ProtectedRoute>} />
          <Route path="/termine" element={<ProtectedRoute><MainLayout><AppointmentsPage /></MainLayout></ProtectedRoute>} />
          <Route path="/checkin" element={<CheckInPage />} />
          <Route path="/check-in" element={<ProtectedRoute><MainLayout><CheckInPage /></MainLayout></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><MainLayout><ChatPage /></MainLayout></ProtectedRoute>} />
          <Route path="/bestellen" element={<ProtectedRoute><MainLayout><OrderPage /></MainLayout></ProtectedRoute>} />
          <Route path="/uebergabe" element={<ProtectedRoute><MainLayout><HandoverPage /></MainLayout></ProtectedRoute>} />
          <Route path="/benachrichtigungen" element={<ProtectedRoute><MainLayout><NotificationsPage /></MainLayout></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout><AdminPanel /></AdminLayout></AdminRoute>} />
          <Route path="/master" element={<MasterRoute><AdminLayout><MasterPanel /></AdminLayout></MasterRoute>} />
          <Route path="/master/mactive" element={<MasterRoute><MActiveFullscreenPage /></MasterRoute>} />

          {/* M-Active — Workshop-System (Master + Gewerbekunden) */}
          {/* M-Active ausgelagert — separate URL, Vollbild-Einbettung via /master/mactive */}

          {/* Fallback */}
          <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <div className="App bg-[#161313] min-h-screen">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SiteSettingsProvider>
              <SeoOverridesProvider>
                <SearchConsoleVerification />
                <AppRoutes />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: '#231f1f',
                      color: '#fff',
                      border: '1px solid rgba(96, 94, 94, 0.3)',
                    },
                  }}
                />
              </SeoOverridesProvider>
            </SiteSettingsProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;
